import { useState, useEffect, useRef } from 'react';
import { getAllSeo, createSeo, updateSeo, deleteSeo } from '../../../services/seoService';
// @ts-ignore
import uploadService from '../../../services/uploadService';

interface SeoItem {
  _id: string;
  route: string;
  pageName: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string[];
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  canonicalUrl: string;
  robots: string;
  createdAt: string;
  updatedAt: string;
}

// Cache ngắn hạn tránh double-fetch khi React Strict Mode remount
const seoListCache: { page: number; search: string; data: { items: SeoItem[]; totalPages: number } | null; ts: number } = {
  page: 0,
  search: '',
  data: null,
  ts: 0
};
const SEO_CACHE_TTL_MS = 3000;

const getInitialFromCache = () => {
  const hit = seoListCache.data && seoListCache.page === 1 && seoListCache.search === '' && Date.now() - seoListCache.ts < SEO_CACHE_TTL_MS;
  return hit ? { items: seoListCache.data!.items, totalPages: seoListCache.data!.totalPages } : null;
};

const SEOManagement = () => {
  const cached = getInitialFromCache();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSeo, setSelectedSeo] = useState<SeoItem | null>(null);
  const [seoList, setSeoList] = useState<SeoItem[]>(cached?.items ?? []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(cached?.totalPages ?? 1);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    route: '',
    pageName: '',
    metaTitle: '',
    metaDescription: '',
    metaKeywords: [] as string[],
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterCard: 'summary_large_image',
    twitterTitle: '',
    twitterDescription: '',
    twitterImage: '',
    canonicalUrl: '',
    robots: 'index, follow'
  });

  const [newKeyword, setNewKeyword] = useState('');
  
  // Image upload states
  const [ogImageFile, setOgImageFile] = useState<File | null>(null);
  const [ogImagePreview, setOgImagePreview] = useState<string>('');
  const [twitterImageFile, setTwitterImageFile] = useState<File | null>(null);
  const [twitterImagePreview, setTwitterImagePreview] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const isSearchInitialMount = useRef(true);

  const fetchSeoList = async (bypassCache = false) => {
    const cached = !bypassCache && seoListCache.data && seoListCache.page === currentPage && seoListCache.search === searchTerm && Date.now() - seoListCache.ts < SEO_CACHE_TTL_MS;
    if (cached && seoListCache.data) {
      setSeoList(seoListCache.data.items);
      setTotalPages(seoListCache.data.totalPages);
      setLoading(false);
      setError(null);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await getAllSeo({ page: currentPage, limit: itemsPerPage, search: searchTerm });
      if (response.success && response.data) {
        const items = response.data.items || [];
        const total = response.data.pagination?.totalPages || 1;
        setSeoList(items);
        setTotalPages(total);
        seoListCache.page = currentPage;
        seoListCache.search = searchTerm;
        seoListCache.data = { items, totalPages: total };
        seoListCache.ts = Date.now();
      }
    } catch (err: any) {
      console.error('Error fetching SEO list:', err);
      setError(err.response?.data?.message || 'Không thể tải danh sách SEO');
      setSeoList([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter SEO list
  const filteredSeoList = seoList.filter(seo => {
    const searchLower = searchTerm.toLowerCase();
    return (
      seo.route.toLowerCase().includes(searchLower) ||
      seo.pageName.toLowerCase().includes(searchLower) ||
      seo.metaTitle.toLowerCase().includes(searchLower)
    );
  });

  // Fetch SEO list khi đổi trang
  useEffect(() => {
    fetchSeoList();
  }, [currentPage]);

  // Debounce search: chỉ chạy khi user gõ, bỏ qua lần mount đầu (tránh load 2 lần)
  useEffect(() => {
    if (isSearchInitialMount.current) {
      isSearchInitialMount.current = false;
      return;
    }
    const timeoutId = setTimeout(() => {
      if (currentPage === 1) {
        fetchSeoList();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Reset form
  const resetForm = () => {
    setFormData({
      route: '',
      pageName: '',
      metaTitle: '',
      metaDescription: '',
      metaKeywords: [],
      ogTitle: '',
      ogDescription: '',
      ogImage: '',
      twitterCard: 'summary_large_image',
      twitterTitle: '',
      twitterDescription: '',
      twitterImage: '',
      canonicalUrl: '',
      robots: 'index, follow'
    });
    setNewKeyword('');
    setSelectedSeo(null);
    setOgImageFile(null);
    setOgImagePreview('');
    setTwitterImageFile(null);
    setTwitterImagePreview('');
  };

  // Open add modal
  const handleAddClick = () => {
    resetForm();
    setIsAddModalOpen(true);
  };

  // Open edit modal
  const handleEditClick = (seo: SeoItem) => {
    setSelectedSeo(seo);
    setFormData({
      route: seo.route,
      pageName: seo.pageName,
      metaTitle: seo.metaTitle || '',
      metaDescription: seo.metaDescription || '',
      metaKeywords: seo.metaKeywords || [],
      ogTitle: seo.ogTitle || '',
      ogDescription: seo.ogDescription || '',
      ogImage: seo.ogImage || '',
      twitterCard: seo.twitterCard || 'summary_large_image',
      twitterTitle: seo.twitterTitle || '',
      twitterDescription: seo.twitterDescription || '',
      twitterImage: seo.twitterImage || '',
      canonicalUrl: seo.canonicalUrl || '',
      robots: seo.robots || 'index, follow'
    });
    // Set previews for existing images
    setOgImagePreview(seo.ogImage || '');
    setTwitterImagePreview(seo.twitterImage || '');
    setOgImageFile(null);
    setTwitterImageFile(null);
    setIsEditModalOpen(true);
  };

  // Handle image file selection
  const handleOgImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setOgImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setOgImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTwitterImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setTwitterImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTwitterImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Upload image helper (objectField: 'ogImage' | 'twitterImage')
  const uploadImage = async (file: File, seoId: string, objectField: 'ogImage' | 'twitterImage'): Promise<string> => {
    const result = await uploadService.uploadFile(file, seoId, 'Seo', objectField);
    if (result.success && result.data) {
      // Backend trả về 'url' trong data, không phải 'publicUrl'
      const imageUrl = result.data.url || result.data.publicUrl;
      if (imageUrl) {
        return imageUrl;
      }
      throw new Error('Upload thành công nhưng không có URL ảnh');
    }
    throw new Error(result.message || result.error || 'Upload ảnh thất bại');
  };

  // Handle keyword management
  const handleAddKeyword = () => {
    const trimmedKeyword = newKeyword.trim();
    if (trimmedKeyword && !formData.metaKeywords.includes(trimmedKeyword)) {
      setFormData(prev => ({
        ...prev,
        metaKeywords: [...prev.metaKeywords, trimmedKeyword]
      }));
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setFormData(prev => ({
      ...prev,
      metaKeywords: prev.metaKeywords.filter(k => k !== keyword)
    }));
  };

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setUploading(true);

    try {
      if (isAddModalOpen) {
        // Create SEO first
        const createResponse = await createSeo(formData);
        if (!createResponse.success || !createResponse.data?._id) {
          throw new Error(createResponse.message || 'Tạo SEO thất bại');
        }

        const seoId = createResponse.data._id;
        let updatedFormData = { ...formData };

        // Upload OG Image if selected
        if (ogImageFile) {
          try {
            const ogImageUrl = await uploadImage(ogImageFile, seoId, 'ogImage');
            updatedFormData.ogImage = ogImageUrl;
          } catch (error: any) {
            console.error('Error uploading OG image:', error);
            throw new Error(`Upload OG image thất bại: ${error.message}`);
          }
        }

        // Upload Twitter Image if selected
        if (twitterImageFile) {
          try {
            const twitterImageUrl = await uploadImage(twitterImageFile, seoId, 'twitterImage');
            updatedFormData.twitterImage = twitterImageUrl;
          } catch (error: any) {
            console.error('Error uploading Twitter image:', error);
            throw new Error(`Upload Twitter image thất bại: ${error.message}`);
          }
        }

        // Update SEO with image URLs if any images were uploaded
        if (ogImageFile || twitterImageFile) {
          await updateSeo(seoId, updatedFormData);
        }

        setMessage({ type: 'success', text: 'Tạo SEO thành công' });
        setIsAddModalOpen(false);
        resetForm();
        fetchSeoList(true);
      } else if (isEditModalOpen && selectedSeo) {
        const seoId = selectedSeo._id;
        let updatedFormData = { ...formData };

        // Upload OG Image if changed
        if (ogImageFile) {
          try {
            const ogImageUrl = await uploadImage(ogImageFile, seoId, 'ogImage');
            updatedFormData.ogImage = ogImageUrl;
          } catch (error: any) {
            console.error('Error uploading OG image:', error);
            throw new Error(`Upload OG image thất bại: ${error.message}`);
          }
        }

        // Upload Twitter Image if changed
        if (twitterImageFile) {
          try {
            const twitterImageUrl = await uploadImage(twitterImageFile, seoId, 'twitterImage');
            updatedFormData.twitterImage = twitterImageUrl;
          } catch (error: any) {
            console.error('Error uploading Twitter image:', error);
            throw new Error(`Upload Twitter image thất bại: ${error.message}`);
          }
        }

        // Update SEO
        const response = await updateSeo(seoId, updatedFormData);
        if (response.success) {
          setMessage({ type: 'success', text: 'Cập nhật SEO thành công' });
          setIsEditModalOpen(false);
          resetForm();
          fetchSeoList(true);
        } else {
          throw new Error(response.message || 'Cập nhật SEO thất bại');
        }
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.message || 'Có lỗi xảy ra'
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa SEO này?')) {
      return;
    }

    try {
      const response = await deleteSeo(id);
      if (response.success) {
        setMessage({ type: 'success', text: 'Xóa SEO thành công' });
        fetchSeoList(true);
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Có lỗi xảy ra khi xóa'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Quản lý SEO</h2>
          <p className="text-gray-500 mt-1">Cấu hình SEO cho các trang trên website</p>
        </div>
        <button
          onClick={handleAddClick}
          className="inline-flex items-center gap-2 bg-[#2A4B7C] text-white px-5 py-2.5 rounded-xl hover:bg-[#1e3a5f] transition-colors shadow-sm"
        >
          <i className="ri-add-line text-lg"></i>
          <span>Thêm SEO mới</span>
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${
          message.type === 'success'
            ? 'bg-emerald-50 border border-emerald-100 text-emerald-800'
            : 'bg-red-50 border border-red-100 text-red-800'
        }`}>
          <i className={`text-xl ${message.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}`}></i>
          <span>{message.text}</span>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
        <div className="relative">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
          <input
            type="text"
            placeholder="Tìm kiếm theo route, tên trang, meta title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-gray-50 border-0 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#2A4B7C]/20 focus:bg-white transition-all"
          />
        </div>
      </div>

      {/* SEO List - Card Grid */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#2A4B7C]/5 mb-4">
              <i className="ri-loader-4-line text-2xl text-[#2A4B7C] animate-spin"></i>
            </div>
            <p className="text-gray-500 font-medium">Đang tải danh sách SEO...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-6">
            <div className="flex items-center gap-3 text-red-700">
              <i className="ri-error-warning-line text-2xl"></i>
              <p>{error}</p>
            </div>
          </div>
        ) : filteredSeoList.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gray-100 mb-4">
              <i className="ri-search-line text-4xl text-gray-400"></i>
            </div>
            <p className="text-gray-600 font-medium mb-1">Chưa có cấu hình SEO nào</p>
            <p className="text-sm text-gray-400 mb-6">Nhấn "Thêm SEO mới" để bắt đầu</p>
            <button
              onClick={handleAddClick}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#2A4B7C] text-white rounded-xl hover:bg-[#1e3a5f] transition-colors"
            >
              <i className="ri-add-line"></i>
              Thêm SEO mới
            </button>
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filteredSeoList.map((seo) => (
                <div
                  key={seo._id}
                  className="group bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md hover:border-[#2A4B7C]/20 transition-all duration-300"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="min-w-0 flex-1">
                        <code className="block text-xs font-mono text-[#2A4B7C] bg-[#2A4B7C]/8 px-2.5 py-1.5 rounded-lg mb-2 truncate" title={seo.route}>
                          {seo.route}
                        </code>
                        <h3 className="text-base font-semibold text-gray-900 line-clamp-1" title={seo.pageName}>
                          {seo.pageName}
                        </h3>
                      </div>
                      <div className="flex gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEditClick(seo)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-[#2A4B7C] hover:bg-[#2A4B7C]/10 transition-colors"
                          title="Chỉnh sửa"
                        >
                          <i className="ri-pencil-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(seo._id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                          title="Xóa"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-4 min-h-[2.5rem]" title={seo.metaTitle || undefined}>
                      {seo.metaTitle || '— Chưa có meta title'}
                    </p>
                    {seo.metaKeywords && seo.metaKeywords.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {seo.metaKeywords.slice(0, 5).map((kw, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center text-xs px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-medium"
                          >
                            {kw}
                          </span>
                        ))}
                        {seo.metaKeywords.length > 5 && (
                          <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-lg bg-gray-200 text-gray-600 font-medium">
                            +{seo.metaKeywords.length - 5}
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic">Chưa có keywords</p>
                    )}
                  </div>
                  <div className="px-5 py-3 bg-gray-50/80 border-t border-gray-100 flex justify-end gap-2 sm:hidden">
                    <button
                      onClick={() => handleEditClick(seo)}
                      className="text-sm font-medium text-[#2A4B7C] hover:underline"
                    >
                      Sửa
                    </button>
                    <button
                      onClick={() => handleDelete(seo._id)}
                      className="text-sm font-medium text-red-500 hover:underline"
                    >
                      Xóa
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-2">
                <p className="text-sm text-gray-500">
                  Trang <span className="font-medium text-gray-700">{currentPage}</span> / {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <i className="ri-arrow-left-s-line mr-1 align-middle"></i>
                    Trước
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    Sau
                    <i className="ri-arrow-right-s-line ml-1 align-middle"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(isAddModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {isAddModalOpen ? 'Thêm SEO mới' : 'Chỉnh sửa SEO'}
              </h3>
              <button
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditModalOpen(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">Thông tin cơ bản</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Route <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.route}
                    onChange={(e) => setFormData(prev => ({ ...prev, route: e.target.value }))}
                    placeholder="/ve-chung-toi"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Route của trang (ví dụ: /, /ve-chung-toi, /tin-tuc)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên trang <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.pageName}
                    onChange={(e) => setFormData(prev => ({ ...prev, pageName: e.target.value }))}
                    placeholder="Về chúng tôi"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>
              </div>

              {/* Meta Tags */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">Meta Tags</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
                  <input
                    type="text"
                    value={formData.metaTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, metaTitle: e.target.value }))}
                    placeholder="Tiêu đề SEO"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                  <textarea
                    value={formData.metaDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, metaDescription: e.target.value }))}
                    placeholder="Mô tả SEO"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meta Keywords</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      placeholder="Nhập keyword và nhấn Enter"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleAddKeyword}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Thêm
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.metaKeywords.map((keyword, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                      >
                        {keyword}
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyword(keyword)}
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Open Graph */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">Open Graph</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OG Title</label>
                  <input
                    type="text"
                    value={formData.ogTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, ogTitle: e.target.value }))}
                    placeholder="Open Graph Title"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OG Description</label>
                  <textarea
                    value={formData.ogDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, ogDescription: e.target.value }))}
                    placeholder="Open Graph Description"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">OG Image</label>
                  
                  {/* Image Preview */}
                  {(ogImagePreview || formData.ogImage) && (
                    <div className="mb-3">
                      <div className="relative inline-block">
                        <img
                          src={ogImagePreview || formData.ogImage}
                          alt="OG Image preview"
                          className="max-w-full h-32 object-contain border border-gray-300 rounded-lg"
                        />
                        {ogImageFile && (
                          <button
                            type="button"
                            onClick={() => {
                              setOgImageFile(null);
                              setOgImagePreview('');
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* File Input */}
                  <div className="flex gap-2">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleOgImageChange}
                        className="hidden"
                      />
                      <div className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-center cursor-pointer">
                        <i className="ri-image-add-line mr-2"></i>
                        {ogImageFile ? 'Đổi ảnh' : (formData.ogImage ? 'Đổi ảnh' : 'Chọn ảnh')}
                      </div>
                    </label>
                    {formData.ogImage && !ogImageFile && (
                      <input
                        type="text"
                        value={formData.ogImage}
                        onChange={(e) => setFormData(prev => ({ ...prev, ogImage: e.target.value }))}
                        placeholder="Hoặc nhập URL"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                      />
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Chọn ảnh từ máy tính hoặc nhập URL</p>
                </div>
              </div>

              {/* Twitter Card */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">Twitter Card</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Card Type</label>
                  <select
                    value={formData.twitterCard}
                    onChange={(e) => setFormData(prev => ({ ...prev, twitterCard: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  >
                    <option value="summary">Summary</option>
                    <option value="summary_large_image">Summary Large Image</option>
                    <option value="app">App</option>
                    <option value="player">Player</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Title</label>
                  <input
                    type="text"
                    value={formData.twitterTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, twitterTitle: e.target.value }))}
                    placeholder="Twitter Title"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Description</label>
                  <textarea
                    value={formData.twitterDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, twitterDescription: e.target.value }))}
                    placeholder="Twitter Description"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Twitter Image</label>
                  
                  {/* Image Preview */}
                  {(twitterImagePreview || formData.twitterImage) && (
                    <div className="mb-3">
                      <div className="relative inline-block">
                        <img
                          src={twitterImagePreview || formData.twitterImage}
                          alt="Twitter Image preview"
                          className="max-w-full h-32 object-contain border border-gray-300 rounded-lg"
                        />
                        {twitterImageFile && (
                          <button
                            type="button"
                            onClick={() => {
                              setTwitterImageFile(null);
                              setTwitterImagePreview('');
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <i className="ri-close-line text-sm"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* File Input */}
                  <div className="flex gap-2">
                    <label className="flex-1 cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleTwitterImageChange}
                        className="hidden"
                      />
                      <div className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-center cursor-pointer">
                        <i className="ri-image-add-line mr-2"></i>
                        {twitterImageFile ? 'Đổi ảnh' : (formData.twitterImage ? 'Đổi ảnh' : 'Chọn ảnh')}
                      </div>
                    </label>
                    {formData.twitterImage && !twitterImageFile && (
                      <input
                        type="text"
                        value={formData.twitterImage}
                        onChange={(e) => setFormData(prev => ({ ...prev, twitterImage: e.target.value }))}
                        placeholder="Hoặc nhập URL"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                      />
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Chọn ảnh từ máy tính hoặc nhập URL</p>
                </div>
              </div>

              {/* Advanced */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-900">Cài đặt nâng cao</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Canonical URL</label>
                  <input
                    type="url"
                    value={formData.canonicalUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, canonicalUrl: e.target.value }))}
                    placeholder="https://stackway.vn/ve-chung-toi"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Robots</label>
                  <input
                    type="text"
                    value={formData.robots}
                    onChange={(e) => setFormData(prev => ({ ...prev, robots: e.target.value }))}
                    placeholder="index, follow"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">Ví dụ: index, follow hoặc noindex, nofollow</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setIsEditModalOpen(false);
                    resetForm();
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-6 py-2 bg-[#2A4B7C] text-white rounded-lg hover:bg-[#1e3a5f] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Đang xử lý...
                    </>
                  ) : (
                    isAddModalOpen ? 'Tạo' : 'Cập nhật'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SEOManagement;
